<template>
  <CellValue
    v-if="!item.isNestedTask"
    :user-has-read-access="readable"
    :user-has-update-access="updatable"
    cell-value=""
    inherit-cursor-style
  >
    <DefineTemplate>
      <OkrGrade
        :color="item.gradeColor"
        :grade="item.gradeToUse"
        :grade-mode="item.gradeMode"
        :is-contribute="item.contribute"
        :show-multiplier="showMultiplier"
        :type-id="item.typeId"
        :weight="item.weight"
        :weight-multiplier="item.weightMultiplier"
        additional-wrapper-on-value
        class="gc-Grade"
        hide-weight-icon
        multiplier-icon-name="table-grade-multiplier"
        multiplier-icon-size="16"
        type="default-next"
        weight-is-dimmed
      />
    </DefineTemplate>

    <template v-if="isTask">
      <ReuseTemplate
        :id="`gr${uid}`"
        :class="{ 'gc-Grade-updatable': updatable }"
        :readonly="!updatable"
        @click="onGradeClick"
      />

      <GradeSelect
        v-if="updatable && editInitialised"
        v-model:show="showSelect"
        :element-target-periods="
          item[OKR_ELEMENT_KEYS.ELEMENT_TARGET_PERIODS] ||
          DEFAULT_MILESTONES_SETTINGS[OKR_ELEMENT_KEYS.ELEMENT_TARGET_PERIODS]
        "
        :objective="item"
        :to-selector="`#gr${uid}`"
        :view-config
        @save="emit('save-grade', $event)"
        @update:is-loading="emit('loading', $event)"
        @error-on-save="emit('error-on-save-grade', $event)"
      />
    </template>

    <CheckinModalTrigger
      v-else
      :entry-point="CHECKIN_MODAL_ENTRY_POINTS.CREATE"
      :okr-element="item"
      @open-checkin-modal="emit('open-checkin-modal', $event)"
    >
      <template #default="{ onTriggerClick, canOpenCheckinModal }">
        <ReuseTemplate
          :class="{ 'gc-Grade-updatable': canOpenCheckinModal }"
          :readonly="!canOpenCheckinModal"
          @click="onTriggerClick"
        />
      </template>
    </CheckinModalTrigger>
  </CellValue>
</template>

<script setup>
import { createReusableTemplate } from '@vueuse/core'
import { computed, nextTick, ref, watch } from 'vue'

import { CHECKIN_MODAL_ENTRY_POINTS } from '@/utils/check-ins/check-ins'
import { isJiraIssueOrWorkItem } from '@/utils/objective-types'
import { DEFAULT_MILESTONES_SETTINGS } from '@/utils/okr-element-values'
import { objectOrNullProp } from '@/utils/prop-validators'
import { uid as createUid } from '@/utils/uid'
import { OKR_ELEMENT_KEYS } from '@/utils/view-row-actions'

import CheckinModalTrigger from '@/components/objectives/element-check-in/CheckinModalTrigger'
import GradeSelect from '@/components/objectives/GradeSelect'
import OkrGrade from '@/components/objectives/OkrGrade'
import CellValue from '@/components/objectives/table/cells/CellValue'

const [DefineTemplate, ReuseTemplate] = createReusableTemplate()

defineOptions({
  name: 'GradeCell'
})

const props = defineProps({
  item: {
    type: Object,
    required: true
  },

  readable: {
    type: Boolean
  },

  updatable: {
    type: Boolean
  },

  showMultiplier: {
    type: Boolean,
    default: true
  },

  viewConfig: {
    validator: v => objectOrNullProp(v),
    default: null
  }
})

const emit = defineEmits({
  loading: null,
  'editing-grade': null,
  'error-on-save-grade': null,
  'save-grade': null,
  'open-checkin-modal': null
})

const uid = ref(createUid())
const editInitialised = ref(false)
const showSelect = ref(false)

const isTask = computed(() => isJiraIssueOrWorkItem(props.item))

watch(
  () => props.updatable,
  newValue => {
    if (!newValue && editInitialised.value) {
      editInitialised.value = false
      showSelect.value = false
    }
  },
  { immediate: true }
)

watch(showSelect, newValue => {
  if (!newValue) {
    nextTick(() => {
      editInitialised.value = false
    })
  }
})

const onGradeClick = () => {
  if (props.updatable) {
    if (!editInitialised.value) {
      editInitialised.value = true
    }

    showSelect.value = !showSelect.value
  }
}
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/grade-cell-variables';

.gc-Grade {
  :deep(.okg-MultiplierIcon) {
    margin-right: 4px;
  }
  :deep(.okg-OkrGrade_Value) {
    @extend %grade-cell-first-part-min-width;
  }

  :deep(.okg-OkrGradeValueWrapper) {
    font-weight: fw('semi-bold');
    padding: 2px 6px;
    max-width: 100%;
    display: inline-block;
    overflow: hidden;
    text-overflow: ellipsis;
    border-radius: $border-radius-sm-next;
    vertical-align: middle;
  }

  &-updatable {
    cursor: pointer;
    .o-objective-row:hover & {
      :deep(.okg-OkrGradeValueWrapper) {
        background-color: rgba(var(--rgb-color), 0.2);
      }
    }
  }
}
</style>
